import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getEnv } from 'common/utils';
import { Message, MessageBox } from '@/types/messageBox';
import service from '@/service';
import { RootState } from './index';
import { AppThunk } from '.';

interface MessageBoxState {
  messages: MessageBox;
  notices: MessageBox;
  isMessageBoxShow: boolean;
  curPage: number;
  refreshCount: number;
}

const initialState: MessageBoxState = {
  messages: {
    items: [],
    unreadCount: 0,
    total: 0,
  },
  notices: {
    items: [],
    unreadCount: 0,
    total: 0,
  },
  curPage: 1,

  refreshCount: 0,
  isMessageBoxShow: false,
};
const messageBox = createSlice({
  name: 'messageBox',
  initialState,
  reducers: {
    setMessages(state, action: PayloadAction<Partial<MessageBox>>) {
      state.messages = { ...state.messages, ...action.payload };
    },
    setNotices(state, action: PayloadAction<Partial<MessageBox>>) {
      state.notices = { ...state.notices, ...action.payload };
    },
    setIsMessageBoxShow(state, action: PayloadAction<boolean>) {
      state.isMessageBoxShow = action.payload;
    },
    setCurPage(state, action: PayloadAction<number>) {
      state.curPage = action.payload;
    },
    setRefreshCount(state, action: PayloadAction<number>) {
      state.refreshCount = action.payload;
    },
  },
});
export const { setMessages, setNotices, setIsMessageBoxShow, setCurPage, setRefreshCount } = messageBox.actions;
export const selectIsMessageBoxShow = (state: RootState) => state.messageBox.isMessageBoxShow;
export const selectMessages = (state: RootState) => state.messageBox.messages;
export const selectNotices = (state: RootState) => state.messageBox.notices;
export const selectCurPage = (state: RootState) => state.messageBox.curPage;
export const selectRefreshCount = (state: RootState) => state.messageBox.refreshCount;

export const getMessages =
  ({ page, pageSize, unRead }): AppThunk =>
  async (dispatch, getState) => {
    const { isInternal } = getEnv();
    const data = await service.message.getMessages({
      page,
      pageSize,
      unRead,
      environment: isInternal ? 'INNER' : 'OUTER',
    });

    data && dispatch(setMessages({ items: data.items, total: data.total, unreadCount: data.unreadCount }));
  };
export const getNotices =
  ({ page, pageSize, unRead }): AppThunk =>
  async (dispatch, getState) => {
    const { isInternal } = getEnv();
    const data = await service.message.getNotices({
      page,
      pageSize,
      unRead,
      environment: isInternal ? 'INNER' : 'OUTER',
    });

    data && dispatch(setNotices({ items: data.items, total: data.total, unreadCount: data.unreadCount }));
  };
export const unshiftMessage =
  (message: Message): AppThunk =>
  async (dispatch, getState) => {
    //将一条消息或通知插入到消息盒子的第一条
    const { messages } = getState().messageBox;
    let { items, total, unreadCount } = messages;
    items.unshift(message);
    total += 1;
    unreadCount += 1;

    dispatch(setMessages({ items, total, unreadCount }));
  };
export const unshiftNotice =
  (notice: Message): AppThunk =>
  async (dispatch, getState) => {
    //将一条消息或通知插入到消息盒子的第一条
    const { notices } = getState().messageBox;
    let { items, total, unreadCount } = notices;
    items.unshift(notice);
    total += 1;
    unreadCount += 1;
    dispatch(setNotices({ items, total, unreadCount }));
  };

export const checkMessageOrNotice = (): AppThunk => async (dispatch, getState) => {
  //检查是否有未读消息或通知
  const { messages, notices } = getState().messageBox;

  return messages.unreadCount + notices.unreadCount > 0;
};
export const clearMessageBox = (): AppThunk => async (dispatch, getState) => {
  //清空消息盒子
  dispatch(setMessages({ items: [], total: 0, unreadCount: 0 }));
};
// 已读一条消息或通知
export const readMessageOrNotice =
  (id: number, type: 'message' | 'notice'): AppThunk =>
  async (dispatch, getState) => {
    //标记消息或通知为已读
    const { messages, notices } = getState().messageBox;

    const target = type === 'message' ? messages : notices;
    const unreadCount = type === 'message' ? messages.unreadCount : notices.unreadCount;

    await service.message.readMessage([id]);
    let { items } = target;
    const index = items.findIndex((item) => item.id === id);

    if (index !== -1) {
      const item = { ...items[index] };
      if (item.isRead) {
        return;
      }

      item['isRead'] = true;
      if (type === 'message') {
        dispatch(
          setMessages({
            items: [...items.slice(0, index), item, ...items.slice(index + 1)],
            unreadCount: unreadCount - 1,
            total: target.total - 1,
          }),
        );
      } else {
        dispatch(
          setNotices({
            items: items,
            unreadCount: unreadCount - 1,
            total: target.total - 1,
          }),
        );
      }
    }
  };
// 批量标记为已读
export const readMessagesOrNotices =
  (type: 'message' | 'notice'): AppThunk =>
  async (dispatch, getState) => {
    await service.message.readAllMessages(type);
  };
export default messageBox.reducer;
